import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Button } from '@osd/ui/button';
import { Modal } from '@osd/ui/modal';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/modal — live docs for the `Modal` primitive.
 *
 * Modal handles focus trap, scroll lock, Escape, backdrop click,
 * and focus restoration on close. The compound parts (`Header`,
 * `Title`, `Body`, `Footer`) keep consumers from hand-rolling
 * surface markup.
 */
export const Route = createFileRoute('/ui/_layout/modal')({
  component: ModalPage,
});

function ModalPage() {
  // Demo state — three separate modals on one page.
  const [basic, setBasic] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [focused, setFocused] = useState(false);
  const focusTargetRef = useRef<HTMLInputElement>(null);

  return (
    <Stack gap={6}>
      <Section
        title="Modal"
        hint="Accessible dialog overlay — portal + focus trap + scroll lock + Escape + backdrop click"
      >
        <Stack gap={3}>
          <Button onClick={() => setBasic(true)}>Open basic modal</Button>
          <Button onClick={() => setConfirm(true)} variant="danger">
            Open confirm-destructive modal
          </Button>
          <Button onClick={() => setFocused(true)}>Open modal with initial focus on a field</Button>
        </Stack>
      </Section>

      <Section title="Basic modal" hint="Open + close via state">
        <Modal open={basic} onClose={() => setBasic(false)} title="Welcome" size="md">
          <Modal.Body>
            <Text>
              Modals render to <code>document.body</code> via a React portal — they escape any
              stacking-context parent. Press Escape or click the backdrop to close.
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setBasic(false)}>Got it</Button>
          </Modal.Footer>
        </Modal>
      </Section>

      <Section title="Confirm-destructive modal" hint="Destructive action pattern">
        <Modal
          open={confirm}
          onClose={() => setConfirm(false)}
          title="Delete this invite token?"
          size="sm"
        >
          <Modal.Body>
            <Text>
              Once revoked, the token can't be re-issued. Existing sessions signed with it will be
              forced to re-authenticate.
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setConfirm(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => setConfirm(false)}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Section>

      <Section
        title="Modal with initial focus"
        hint="Pass a ref to focus a specific element on open"
      >
        <Modal
          open={focused}
          onClose={() => setFocused(false)}
          title="Confirm email"
          size="md"
          initialFocusRef={focusTargetRef}
        >
          <Modal.Body>
            <Stack gap={3}>
              <Text>Type your email to confirm:</Text>
              <input
                ref={focusTargetRef}
                type="email"
                placeholder="you@example.com"
                className="osd-input"
              />
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setFocused(false)}>Cancel</Button>
            <Button onClick={() => setFocused(false)}>Confirm</Button>
          </Modal.Footer>
        </Modal>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Basic modal"
            code={`const [open, setOpen] = useState(false);

<Modal open={open} onClose={() => setOpen(false)} title="Welcome">
  <Modal.Body>Hello from a portal.</Modal.Body>
  <Modal.Footer>
    <Button onClick={() => setOpen(false)}>Got it</Button>
  </Modal.Footer>
</Modal>
`}
          />

          <CodeBlock
            language="tsx"
            title="Confirm-destructive modal"
            code={`<Modal open={open} onClose={() => setOpen(false)} title="Delete?">
  <Modal.Body>This action is irreversible.</Modal.Body>
  <Modal.Footer>
    <Button onClick={() => setOpen(false)}>Cancel</Button>
    <Button variant="danger" onClick={onDelete}>Delete</Button>
  </Modal.Footer>
</Modal>
`}
          />

          <CodeBlock
            language="tsx"
            title="With initial focus + closeOnEscape=false"
            code={`const ref = useRef<HTMLInputElement>(null);

<Modal
  open={open}
  onClose={() => setOpen(false)}
  title="Confirm email"
  initialFocusRef={ref}
  closeOnEscape={false}
>
  <Modal.Body>
    <input ref={ref} type="email" />
  </Modal.Body>
</Modal>
`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
